import { Injectable, isDevMode } from '@angular/core';
import { DocumentViewerService } from '@lib/components/document-viewer/document-viewer-panel/document-viewer-panel.service';
import { DocumentsService, PDF_BY_ID } from '@lib/services/documents.service';
import _ from 'lodash';
import {
  AdequationConfig,
  CreateFicheConseilInput,
  FicheConseil,
  FicheConseilInput,
  FicheConseilState,
  FieldConfig,
  Investisseur,
  InvestisseurFicheObjectif,
  ModeDeGestion,
  Produit,
  ProduitConseil,
  ProduitSpecificConfig,
  StorageFile,
} from 'projects/fiche-conseil-lib/src/lib/models/generated/graphql';

import { DatePipe } from '@angular/common';
import { ObjectifsService } from '@lib/services/objectifs.service';
import { gql } from 'apollo-angular';
import { ApplicationInsightsService, deepCopy, QueryManagerService } from 'finzzle-angular-utils';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';
import {
  DEFAULT_DISPONIBILITE_OPTIONS,
  DEFAULT_TOLERANCE_RISQUE_OPTIONS,
} from 'src/app/components/declare-fiche-conseil/steps/exigences-step/default-option';
import { AllocationService } from 'src/app/services/allocation.service';
import { ConseilConfigService } from 'src/app/services/conseil-config.service';
import { FicheStateMachine } from 'src/app/services/FicheStateMachine';
import { FormConfigService } from 'src/app/services/form-config.service';
import { FondsEvenementielService } from 'src/app/services/fonds-evenementiel.service';
import { LoadingService } from 'src/app/services/loading-service';
import { OptionAutomatiqueService } from 'src/app/services/option-automatique.service';

export const investisseurAllFieldsFragment = gql`
  fragment investisseurAllFields on Investisseur {
    id
    code
    civiliteCode
    dateNaissance
    denomination
    displayName
    nom
    prenom
    # patrimoine {
    #   dateMiseAJourAtlas
    #   epargneFinanceMontantTotal
    #   epargneImmoMontantHorsRP
    #   revenuAnnuelTotal
    #   capaciteEpargneMensuel
    #   besoinEpargnePrecaution
    # }
    situationPersonnelle {
      dateMiseAJourAtlas
      profession
      enfantACharge
      etatCivilTypeCode
      etatCivilTypeLibelle
      etatCivilTypeRegimeCode
      etatCivilTypeRegimeLibelle
      etatCivilDisplay
      categorieSocioProfessionnelleCode
    }
    profil {
      # dateMiseAJourAtlas
      profilQpiLibelle
      # experienceLibelle
      profilQpiCode
      # experienceCode
      hasTolerancePerte
    }

    statutCodeNavigation {
      libelle
      code
    }
    creationDate
  }
`;

export const ficheAfterConnaissanceClientFragment = gql`
  fragment ficheAfterConnaissanceClient on FicheConseil {
    id
    dateModification
    investisseurType
    investisseur {
      ...investisseurAllFields
    }
    coInvestisseur {
      ...investisseurAllFields
    }
  }
  ${investisseurAllFieldsFragment}
`;

export const ficheAfterInvestisseurValidationFragment = gql`
  fragment ficheAfterInvestisseurValidation on FicheConseil {
    id
    dateModification
    investisseurType
    objectifs {
      id
      categorieCode
      categorieLibelle
      isSelected
      objectifCode
      objectifLibelle
    }
  }
`;

export const ficheAfterRequierementsAndNeedsValidationFragment = gql`
  fragment ficheAfterRequierementsAndNeedsValidationFragment on FicheConseil {
    id
    dateModification
    investisseurType
  }
`;

export const ficheAllFieldsFragment = gql`
  fragment ficheAllFields on FicheConseil {
    id
    statut
    dateCreation
    contratId
    numeroContrat
    transactionPersonnelle
    disponibiliteEpargne
    disponibiliteEpargneOptions {
      id
      value
      label
    }
    toleranceRisque
    toleranceRisqueOptions {
      id
      value
      label
    }
    allocationPrecision
    arbitrageLibreSupportsSortants
    arbitrageLibreSupportsEntrants
    allocationVersementOption
    allocationTransfertOption
    portefeuilleOption
    motivationOperation
    modeDeGestion {
      code
      libelle
    }
    fondsEvenementiels {
      id
      code
      libelle
      isSelected
    }
    optionsAutomatiques {
      id
      libelle
      isSelected
    }
    allocation {
      id
      libelle
      niveauDeRisque
      modeDeGestionCode
    }
    allocationConsultantFileId
    allocationConsultantFile {
      id
      fileName
      fileType
      fileExtension
      isBlob
    }
    consultant {
      civiliteCode
      code
      displayName
      nom
      prenom
    }
    investisseur {
      ...investisseurAllFields
    }
    coInvestisseur {
      ...investisseurAllFields
    }
    natureOperation {
      id
      libelle
    }
    horizonPlacement {
      value
      horizonRetraite
      justification
    }
    libelle
    dateModification
    enveloppe {
      code
      libelle
    }
    produit {
      id
      libelle
      partenaireId
      partenaireNom
    }
    versements {
      souscriptionAvecTransfert
      versementInitial {
        fraisVersement
        montantBrut
        niveauDeRisque
      }
      versementComplementaire {
        fraisVersement
        montantBrut
      }
      versementProgramme {
        frequence
        isFrequenceEditable
        isMontantBrutEditable
        isFraisVersementEditable
        avecChangementCoordonneesBancaires
        avecChangementAllocation
        versement {
          fraisVersement
          montantBrut
          niveauDeRisque
        }
      }
      avecVersementProgramme
    }
    avecMiseEnPlaceOuModificationVP
    avecArbitragesProgrammes
    vpModeOptions
    investisseurType
    statut
    conseilConfigId
    conseilConfigLibelle
    formConfig {
      fieldConfigs {
        id
        fieldKey
        isVisible
        validators {
          id
          errorMessage
          parameters {
            id
            key
            value
          }
          validatorKey
        }
      }
      lastChangeDate
    }
    objectifs {
      id
      categorieCode
      categorieLibelle
      isSelected
      objectifCode
      objectifLibelle
    }
    arbitrageDuplicationContext {
      sourceFicheId
      objectifsIntersectionApplied
      sourceObjectifs {
        id
        categorieCode
        categorieLibelle
        isSelected
        objectifCode
        objectifLibelle
      }
    }
    precisionComplementaire {
      commentaireEnveloppe
      commentaireAllocation
      commentaireAutre
    }
  }
  ${investisseurAllFieldsFragment}
`;

export const PDF_PREVIEW = gql`
  mutation pdfPreview($fiche: FicheConseilInput!) {
    fichePDFPreview(ficheConseil: $fiche) {
      id
      fileName
      fileExtension
      isBlob
      denomination
      createdDate
      lastModificationDate
      fileConnectionInfo {
        sasUrl
        expiration
        permissions
        blobFileId
      }
      fileNameWithExtension
    }
  }
`;

export const DEPRECATE_STORAGEFILE = gql`
  mutation deprecateStorageFile($storageFileId: String!) {
    deprecateStorageFile(storageFileId: $storageFileId) {
      id
    }
  }
`;

export const DELETE_FICHES_BY_IDS = gql`
  mutation deleteFiches($ficheIds: [String!]!) {
    deleteFiches(fichesIds: $ficheIds)
  }
`;

export const UPDATE_FICHE_CONSEIL = gql`
  mutation updateFicheConseil($newFiche: FicheConseilInput!) {
    updateFicheConseil(newFiche: $newFiche) {
      ...ficheAllFields
    }
  }
  ${ficheAllFieldsFragment}
`;

export const UPDATE_FICHE_CONSEIL_CONNAISSANCE_CLIENT = gql`
  mutation updateFicheConseil($newFiche: FicheConseilInput!) {
    updateFicheConseil(newFiche: $newFiche) {
      ...ficheAfterConnaissanceClient
    }
  }
  ${ficheAfterConnaissanceClientFragment}
`;

export const UPDATE_FICHE_CONSEIL_AFTER_INVESTISSEUR_VALIDATED = gql`
  mutation updateFicheConseil($newFiche: FicheConseilInput!) {
    updateFicheConseil(newFiche: $newFiche) {
      ...ficheAfterInvestisseurValidation
    }
  }
  ${ficheAfterInvestisseurValidationFragment}
`;

export const UPDATE_FICHE_CONSEIL_REQUIREMENTS_AND_NEEDS_VALIDATED = gql`
  mutation updateFicheConseil($newFiche: FicheConseilInput!) {
    updateFicheConseil(newFiche: $newFiche) {
      ...ficheAfterRequierementsAndNeedsValidationFragment
    }
  }
  ${ficheAfterRequierementsAndNeedsValidationFragment}
`;

export const GET_FICHE_BY_ID = gql`
  query fichesById($id: String!) {
    ficheById(id: $id) {
      ...ficheAllFields
    }
  }
  ${ficheAllFieldsFragment}
`;

export const CREATE_FICHE_CONSEIL = gql`
  mutation createFicheConseil($input: CreateFicheConseilInput!) {
    createFicheConseil(input: $input) {
      ...ficheAllFields
    }
  }
  ${ficheAllFieldsFragment}
`;

const CREATE_FROM_OLD = gql`
  mutation createFicheConseilFromOldFiche($ficheId: String!) {
    createFicheConseilFromOldFiche(ficheId: $ficheId) {
      ...ficheAllFields
    }
  }
  ${ficheAllFieldsFragment}
`;

const CREATE_ARBITRAGE_FROM_SOURCE = gql`
  mutation createArbitrageFicheFromSource($ficheId: String!) {
    createArbitrageFicheFromSource(ficheId: $ficheId) {
      ...ficheAllFields
    }
  }
  ${ficheAllFieldsFragment}
`;
@Injectable({
  providedIn: 'root',
})
export class FicheConseilService {
  public currentFicheConseilSubject = new BehaviorSubject<FicheConseil | null>(null);
  private stateMachine = new FicheStateMachine();

  public isInvestisseurLocked: boolean;
  public readonly statesAllowEditInvestisseur = [
    FicheConseilState.DraftCreated,
    FicheConseilState.DraftInvestisseurSelected,
  ];
  constructor(
    private queryManager: QueryManagerService,
    private formService: FormConfigService,
    private documentService: DocumentsService,
    private documentViewerService: DocumentViewerService,
    private objectifsService: ObjectifsService,
    private loadingService: LoadingService,
    private snackbarService: OuiSnackbarService,
    private optionAutomatiqueService: OptionAutomatiqueService,
    private fondsEvenementielService: FondsEvenementielService,
    private allocationService: AllocationService,
    private conseilConfigService: ConseilConfigService,
    private loggerService: ApplicationInsightsService,
    private datePipe: DatePipe
  ) {}

  onFicheIdSelected(ficheId: string) {
    if (ficheId) {
      this.queryManager
        .query<{ ficheById: FicheConseil }>({
          query: GET_FICHE_BY_ID,
          variables: { id: ficheId },
          fetchPolicy: 'network-only',
        })
        .subscribe(result => {
          const fiche = result.data?.ficheById;
          this.setNewFicheData(fiche);
        });
    }
  }

  private setNewFicheData(fiche: FicheConseil | null) {
    if (fiche) {
      this.currentFicheConseilSubject.next(fiche);
      this.conseilConfigService.onConseilConfigSelected(fiche.conseilConfigId);
      this.isInvestisseurLocked = !!fiche.investisseur && !this.statesAllowEditInvestisseur.includes(fiche.statut);
      this.formService.setAdequationConfig(fiche.formConfig ?? null);
    } else {
      this.currentFicheConseilSubject.next(null);
      this.formService.setAdequationConfig(null);
      this.conseilConfigService.reset();
    }
  }

  resetCurrentFicheConseil() {
    this.currentFicheConseilSubject.next(null);
    this.formService.setAdequationConfig(null);
    this.conseilConfigService.reset();
    this.optionAutomatiqueService.setOptionAutomatique(null);
    this.fondsEvenementielService.reset();
  }

  async changeToNextState(): Promise<boolean> {
    if (this.currentFicheConseilSubject.value?.statut) {
      let newStatus: FicheConseilState | undefined = this.stateMachine.getNextState(
        this.currentFicheConseilSubject?.value?.statut
      );

      if (newStatus !== undefined) {
        // can validate current step : update status
        this.currentFicheConseilSubject.next({
          ...this.currentFicheConseilSubject.value,
          statut: newStatus,
        });
        return await this.pushFicheConseilDataToBackEnd(newStatus);
      }
    }
    return false;
  }

  async lastStateOrChangeToNextState(): Promise<boolean> {
    if (this.currentFicheConseilSubject.value?.statut) {
      const isLastState = this.stateMachine.isLastState(this.currentFicheConseilSubject.value.statut);
      if (isLastState) {
        return true;
      }
      return await this.changeToNextState();
    }
    return false;
  }

  returnToPreviousState(targetState: FicheConseilState) {
    if (
      this.currentFicheConseilSubject.value?.statut &&
      this.stateMachine.canGoToPreviousState(this.currentFicheConseilSubject.value.statut, targetState)
    ) {
      this.currentFicheConseilSubject.next({
        ...this.currentFicheConseilSubject.value,
        statut: targetState,
      });
    }
  }

  /**
   * Updates the current fiche conseil data based on the provided key and value.
   * If a selector function is provided, it will be used to select specific data with the new value. (useful for nested objects or arrays)
   * If no selector is provided, the new value
   * is directly set.
   *
   * @param dataKey - The key of the data to be updated in the fiche conseil.
   * @param newValue - The new value to set for the specified data key.
   * @param selector - An optional function to modify the current value before updating.
   */
  onFicheConseilDataEdit(dataKey: string, newValue: any, arrayItemSelector?: (arrayItem: any) => boolean) {
    if (this.currentFicheConseilSubject.value != null) {
      const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
      let isError = false;
      if (!arrayItemSelector) {
        _.set(newFicheConseil, dataKey, newValue);
      } else {
        let dataArray = _.get(newFicheConseil, dataKey);
        if (Array.isArray(dataArray)) {
          let IndexOfitemToUpdate = dataArray.findIndex(arrayItemSelector);
          if (IndexOfitemToUpdate > -1) {
            dataArray[IndexOfitemToUpdate] = newValue;
          } else {
            isError = true;
          }
        } else {
          isError = true;
        }
        if (isError) {
          if (isDevMode()) {
            console.warn('onFicheConseilDataEdit: dataKey not found or not an array');
          }
        } else {
          _.set(newFicheConseil, dataKey, dataArray);
        }
      }

      if (isDevMode()) {
        console.log('fiche update. new value  on : ', dataKey, newFicheConseil);
      }

      this.currentFicheConseilSubject.next(newFicheConseil);
    }
  }

  async pushFicheConseilDataToBackEnd(statut: FicheConseilState | undefined = undefined): Promise<boolean> {
    if (!this.currentFicheConseilSubject.value) {
      return false;
    }
    let mutation = UPDATE_FICHE_CONSEIL;
    switch (statut) {
      case FicheConseilState.DraftInvestisseurValidated:
        mutation = UPDATE_FICHE_CONSEIL_AFTER_INVESTISSEUR_VALIDATED; // creer nouvelle mutation
        break;
      case FicheConseilState.DraftKycValidated:
        mutation = UPDATE_FICHE_CONSEIL_CONNAISSANCE_CLIENT;
        break;

      case FicheConseilState.DraftRequirementsAndNeedsValidated:
        mutation = UPDATE_FICHE_CONSEIL_REQUIREMENTS_AND_NEEDS_VALIDATED;
        break;

      default:
        break;
    }

    const newFiche = deepCopy(this.currentFicheConseilSubject.value);
    const result = await firstValueFrom(
      this.queryManager.mutate<{ updateFicheConseil: FicheConseil }>({
        mutation: mutation,
        variables: { newFiche },
      })
    );
    const updatedFiche = result.data?.updateFicheConseil;

    if (updatedFiche) {
      const fiche = this.mergeUpdateFicheConseil(this.currentFicheConseilSubject.value, updatedFiche);
      this.isInvestisseurLocked = !!fiche.investisseur && !this.statesAllowEditInvestisseur.includes(fiche.statut);
      this.currentFicheConseilSubject.next(deepCopy(fiche));
      return true;
    }
    return false;
  }

  /**
   * Updates the given fiche conseil with the properties from the partial fiche conseil.
   * The properties that are not present in the partial fiche conseil will remain unchanged.
   * If the value of a property is null, undefined or an empty array, it will be ignored.
   * @param fiche the fiche conseil to update
   * @param updatedFiche the partial fiche conseil with the new values
   * @returns the updated fiche conseil
   */
  private mergeUpdateFicheConseil(fiche: FicheConseil, updatedFiche: Partial<FicheConseil>): FicheConseil {
    const updated: FicheConseil = { ...fiche };

    for (const key in updatedFiche) {
      if (key in fiche) {
        const value = updatedFiche[key as keyof FicheConseil];
        if (value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0)) {
          updated[key as keyof FicheConseil] = value;
        }
      }
    }

    return updated;
  }

  //FIXME: transfer to DocumentsService in library
  public async downloadPDF(ficheId: string | undefined, fileName: string | undefined = undefined) {
    let idToExport;
    if (ficheId) {
      idToExport = ficheId;
    } else {
      idToExport = this.currentFicheConseilSubject.value?.id;
    }

    if (idToExport) {
      let fileData = await firstValueFrom(
        this.queryManager.mutate<{ fichePDF: StorageFile }>({
          mutation: PDF_BY_ID,
          variables: { id: idToExport },
          fetchPolicy: 'network-only',
        })
      );
      if (fileData.data?.fichePDF?.fileConnectionInfo?.sasUrl) {
        const docName = fileName ?? fileData.data.fichePDF.denomination ?? fileData.data.fichePDF.fileName;
        this.documentService.downloadDocumentFromUrl(
          fileData.data.fichePDF.fileConnectionInfo.sasUrl,
          docName + '.' + fileData.data.fichePDF.fileExtension
        );
      }
    }
  }

  public async deleteFiches(ficheIds: string[]) {
    let deleteResult = await firstValueFrom(
      this.queryManager.mutate<{ deleteFiches: boolean }>({
        mutation: DELETE_FICHES_BY_IDS,
        variables: { ficheIds: ficheIds },
        fetchPolicy: 'network-only',
      })
    );
    if (!deleteResult.data?.deleteFiches) {
      this.snackbarService.open('Une erreur est survenue lors de la suppression de la fiche', 'error', 5000, {
        horizontal: 'left',
        vertical: 'bottom',
      });
    } else {
      this.snackbarService.open('La fiche a bien été supprimée', 'success', 5000, {
        horizontal: 'left',
        vertical: 'bottom',
      });
    }
  }

  public async displayPDF(ficheId: string | undefined) {
    let idToDisplay;
    if (ficheId) {
      idToDisplay = ficheId;
    } else {
      idToDisplay = this.currentFicheConseilSubject.value?.id;
    }

    // record event on fiche displayed
    try {
      this.loggerService.logEvent('FicheConseilDisplayed', {
        ficheId: idToDisplay,
      });
    } catch (error) {}

    if (idToDisplay) {
      this.loadingService.startLoading();
      let fileData = await firstValueFrom(
        this.queryManager.mutate<{ fichePDF: StorageFile }>({
          mutation: PDF_BY_ID,
          variables: { id: idToDisplay },
          fetchPolicy: 'network-only',
        })
      );
      if (fileData.data?.fichePDF?.fileConnectionInfo?.sasUrl) {
        if (ficheId) {
          await this.onFicheIdSelected(ficheId);
          this.currentFicheConseilSubject
            .pipe(
              filter((fiche): fiche is FicheConseil => fiche !== null),
              take(1)
            )
            .subscribe(fiche => {
              this.loggerService.logEvent('ficheConseilDisplayed', { ficheId });

              this.loadingService.stopLoading();

              this.documentViewerService.openSimpleDocumentViewer(
                {
                  storageFile: fileData?.data?.fichePDF,
                },
                fiche
              );
            });
        }
      }
    }
  }

  public async previewPDF() {
    const fiche = this.currentFicheConseilSubject.value;
    if (fiche) {
      this.loadingService.startLoading();
      let fileData = await firstValueFrom(
        this.queryManager.mutate<{ fichePDFPreview: StorageFile }>({
          mutation: PDF_PREVIEW,
          variables: { fiche: fiche as FicheConseilInput },
          fetchPolicy: 'network-only',
        })
      );
      if (fileData.data?.fichePDFPreview?.fileConnectionInfo?.sasUrl) {
        this.loadingService.stopLoading();
        this.documentViewerService.openSimpleDocumentViewer(
          {
            storageFile: fileData.data.fichePDFPreview,
          },
          fiche
        );
      }
    }
  }
  /**
   * Set the disponibiliteEpargneOptions to the default values (hardcoded)
   * we use this for compatibilty purpose:
   * FIXME: consider removing this method and use only the db values
   */
  public setDefaultDisponibiliteEpargneOptions() {
    if (this.currentFicheConseilSubject.value != null) {
      const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
      _.set(newFicheConseil, 'disponibiliteEpargneOptions', DEFAULT_DISPONIBILITE_OPTIONS);
      if (isDevMode()) {
        console.log('fiche update. new value  on : ', 'disponibiliteEpargneOptions', newFicheConseil);
      }
      this.currentFicheConseilSubject.next(newFicheConseil);
    }
  }
  /**
   * Set the toleranceRisqueOptions to the default values (hardcoded)
   * we use this for compatibilty purpose
   * * FIXME: consider removing this method and use only the db values
   */
  public setDefaultToleranceRisqueOptions() {
    if (this.currentFicheConseilSubject.value != null) {
      const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
      _.set(newFicheConseil, 'toleranceRisqueOptions', DEFAULT_TOLERANCE_RISQUE_OPTIONS);
      if (isDevMode()) {
        console.log('fiche update. new value  on : ', 'toleranceRisqueOptions', newFicheConseil);
      }
      this.currentFicheConseilSubject.next(newFicheConseil);
    }
  }
  public editConnaisanceClient(clients: { investisseur: Investisseur; coInvestisseur?: Investisseur }) {
    if (this.currentFicheConseilSubject.value != null) {
      if (this.currentFicheConseilSubject.value != null) {
        const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
        _.set(newFicheConseil, 'investisseur.profil', clients.investisseur.profil);
        _.set(newFicheConseil, 'investisseur.situationPersonnelle', clients.investisseur.situationPersonnelle);
        _.set(newFicheConseil, 'investisseur.dateNaissance', clients.investisseur.dateNaissance);
        const name = clients.investisseur.personnePhysique?.[0]?.nom ?? ' - ';
        const firstName = clients.investisseur.personnePhysique?.[0]?.prenom ?? ' - ';
        _.set(newFicheConseil, 'investisseur.nom', name);
        _.set(newFicheConseil, 'investisseur.prenom', firstName);
        const displayName = name + ' ' + firstName;
        _.set(newFicheConseil, 'investisseur.displayName', displayName);

        if (newFicheConseil.coInvestisseur) {
          _.set(newFicheConseil, 'coInvestisseur.profil', clients.coInvestisseur?.profil);
          _.set(newFicheConseil, 'coInvestisseur.situationPersonnelle', clients.coInvestisseur?.situationPersonnelle);
          _.set(newFicheConseil, 'coInvestisseur.dateNaissance', clients.coInvestisseur?.dateNaissance);
          const name = clients.coInvestisseur?.personnePhysique?.[0]?.nom ?? ' - ';
          const firstName = clients.coInvestisseur?.personnePhysique?.[0]?.prenom ?? ' - ';
          _.set(newFicheConseil, 'coInvestisseur.nom', name);
          _.set(newFicheConseil, 'coInvestisseur.prenom', firstName);
          const displayName = name + ' ' + firstName;
          _.set(newFicheConseil, 'coInvestisseur.displayName', displayName);

        }
        if (isDevMode()) {
          console.log('fiche update. new value  on : ', 'Edit Connaisance Client', newFicheConseil);
        }
        this.currentFicheConseilSubject.next(newFicheConseil);
      }
    }
  }


  /**
   *
   * @param input
   * @returns
   */
  public async createNewFiche(
    input: CreateFicheConseilInput
  ): Promise<{ id: string | undefined; nature_operation: number | undefined } | undefined> {
    // create fiche in backend
    let result = await firstValueFrom(
      this.queryManager.mutate<{ createFicheConseil: FicheConseil }>({
        mutation: CREATE_FICHE_CONSEIL,
        variables: { input },
      })
    );

    //update current fiche (use deep copy to allow future edit of fiche data)
    this.currentFicheConseilSubject.next(deepCopy(result.data?.createFicheConseil) ?? null);

    // record event on fiche created
    try {
      this.loggerService.logEvent('FicheConseilCreated', {
        ficheId: result.data?.createFicheConseil?.id,
      });
    } catch (error) {}

    // return fiche id
    const ficheDTO = {
      id: result.data?.createFicheConseil?.id,
      nature_operation: result.data?.createFicheConseil?.natureOperation?.id,
    };
    return ficheDTO;
  }

  public async createFicheFromCurrentFiche(): Promise<string | undefined> {
    const currentficheId = this.currentFicheConseilSubject.value?.id;
    let result = await firstValueFrom(
      this.queryManager.mutate<{ createFicheConseilFromOldFiche: FicheConseil }>({
        mutation: CREATE_FROM_OLD,
        variables: { ficheId: currentficheId },
      })
    );
    //update current fiche (use deep copy to allow future edit of fiche data)
    const fiche = deepCopy(result.data?.createFicheConseilFromOldFiche);
    if (fiche) {
      this.setNewFicheData(fiche);
    }

    // return fiche id
    return result.data?.createFicheConseilFromOldFiche?.id;
  }

  public async createArbitrageFicheFromSource(ficheId: string): Promise<string | undefined> {
    const result = await firstValueFrom(
      this.queryManager.mutate<{ createArbitrageFicheFromSource: FicheConseil }>({
        mutation: CREATE_ARBITRAGE_FROM_SOURCE,
        variables: { ficheId },
      })
    );

    const fiche = deepCopy(result.data?.createArbitrageFicheFromSource);
    if (fiche) {
      this.setNewFicheData(fiche);
    }

    return fiche?.id;
  }

  public getCurrentFicheConseilLabel(): string {
    return this.getFicheConseilLabel(this.currentFicheConseilSubject.value, this.datePipe);
  }

  public getFicheConseilLabel(fiche: FicheConseil | null, datePipe: DatePipe): string {
    if (!fiche) return '-';
    if (fiche.libelle) return fiche.libelle;
    let label = 'Fiche conseil';
    if (fiche?.investisseur?.nom) {
      label += ` - ${fiche.investisseur.nom}`;
    }
    if (fiche?.coInvestisseur?.nom) {
      label += ` - ${fiche.coInvestisseur.nom}`;
    }
    if (fiche.enveloppe) {
      label += ` - ${fiche.enveloppe.libelle}`;
    }
    if (fiche.natureOperation?.libelle) {
      label += ` - ${fiche.natureOperation.libelle}`;
    }
    if (fiche.dateCreation) {
      label += ` - ${datePipe.transform(fiche.dateCreation, 'ddMMyyyy')}`;
    }
    return label;
  }

  public async onUpdateProduitOnFicheConseil(
    produit: ProduitConseil | null,
    enveloppeConfig: AdequationConfig,
    produitSpecificConfig: ProduitSpecificConfig | null
  ) {
    if (!this.currentFicheConseilSubject.value) return;
    const produitGestionPossibles = produitSpecificConfig?.gestionPossibles ?? produitSpecificConfig?.produit?.gestionPossibles;
    const selectedProduit: Produit | null = produitSpecificConfig?.produit
      ? ({
          ...produitSpecificConfig.produit,
          gestionPossibles: produitGestionPossibles,
        } as Produit)
      : null;
    const ficheId = this.currentFicheConseilSubject.value.id;
    this.formService.setSelectedProduit(selectedProduit);
    const newConfig = this.mergeConfigs(enveloppeConfig, produitSpecificConfig?.config);
    this.formService.setAdequationConfig(newConfig ?? null);
    await this.optionAutomatiqueService.loadOptionsAutomatiquesByProduit(produit?.id);
    await this.fondsEvenementielService.loadFondsEvenementielsByProduit(produit?.id);
    await this.allocationService.loadAllocationsByProduit(produit?.id);
    // Copy the fiche only after the awaits: the config update above triggers synchronous edits
    // (e.g. default fraisVersement) that a copy taken earlier would overwrite.
    if (!this.currentFicheConseilSubject.value || this.currentFicheConseilSubject.value.id !== ficheId) return;
    const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
    _.set(newFicheConseil, 'produit', produit);
    _.set(newFicheConseil, 'formConfig', newConfig);
    this.currentFicheConseilSubject.next(newFicheConseil);
  }

  public onAllocationConsultantFileChanged(fileId: string, fileName: string, fileType: string) {
    if (!this.currentFicheConseilSubject.value) return;
    const deprecatedFileId = this.currentFicheConseilSubject.value.allocationConsultantFileId;
    if (deprecatedFileId) {
      this.deprecateStorageFile(deprecatedFileId);
    }
    const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
    _.set(newFicheConseil, 'allocationConsultantFileId', fileId);
    _.set(newFicheConseil, 'allocationConsultantFile.fileName', fileName);
    _.set(newFicheConseil, 'allocationConsultantFile.fileType', fileType);
    _.set(newFicheConseil, 'allocationConsultantFile.id', fileId);
    _.set(newFicheConseil, 'allocationConsultantFile.fileExtension', fileType);
    _.set(newFicheConseil, 'allocationConsultantFile.isBlob', true);
    _.set(newFicheConseil, 'allocation', null);
    this.currentFicheConseilSubject.next(newFicheConseil);

    if (isDevMode()) {
      console.log('fiche update. new value  on : ', 'New Allocation Consultant File', newFicheConseil);
    }
  }

  public onAllocationConsultantFileDeleted() {
    const newFicheConseil = this.deprecateAllocationConsultantFile();
    if (newFicheConseil) {
      this.currentFicheConseilSubject.next(newFicheConseil);
    }
  }

  private deprecateAllocationConsultantFile() {
    if (!this.currentFicheConseilSubject.value) return;
    const deprecatedFileId = this.currentFicheConseilSubject.value.allocationConsultantFileId;
    if (deprecatedFileId) {
      this.deprecateStorageFile(deprecatedFileId);
    }
    const newFicheConseil = deepCopy(this.currentFicheConseilSubject.value);
    _.set(newFicheConseil, 'allocationConsultantFileId', null);
    _.set(newFicheConseil, 'allocationConsultantFile', null);
    return newFicheConseil;
  }
  /**
   * When the mode de gestion is changed,
   * remove the consultant file if any and the allocationPrecision, versements, and allocation data
   * @returns void
   */
  public onModeDeGestionChanged(mode: ModeDeGestion | null) {
    if (
      !this.currentFicheConseilSubject.value ||
      this.currentFicheConseilSubject.value.modeDeGestion?.code === mode?.code
    )
      return;

    // remove  consultant file if mode de gestion changed
    const ficheConseilUpdated = this.deprecateAllocationConsultantFile() ?? null;

    if (ficheConseilUpdated) {
      // remove allocationPrecision
      _.set(ficheConseilUpdated, 'allocationPrecision', null);
      _.set(ficheConseilUpdated, 'arbitrageLibreSupportsSortants', null);
      _.set(ficheConseilUpdated, 'arbitrageLibreSupportsEntrants', null);
      _.set(ficheConseilUpdated, 'versements.versementInitial.niveauDeRisque', null);
      if (ficheConseilUpdated.versements.avecVersementProgramme) {
        _.set(ficheConseilUpdated, 'versements.versementProgramme.versement.niveauDeRisque', null);
      }
      // remove allocation
      _.set(ficheConseilUpdated, 'allocation', null);
      this.currentFicheConseilSubject.next(ficheConseilUpdated);
    }
    if (isDevMode()) {
      console.log('fiche update. new value  on : ', 'Allocation reset', ficheConseilUpdated);
    }
  }
  /**
   * Merge two AdequationConfig, overriding the enveloppe config with the produit config
   * @param enveloppeConfig The enveloppe config to override
   * @param produitConfig The produit config to override with
   * @returns A new AdequationConfig object with the merged fieldConfigs and the most recent lastChangeDate
   */
  mergeConfigs(
    enveloppeConfig: AdequationConfig,
    produitConfig: AdequationConfig | null | undefined
  ): AdequationConfig {
    if (!produitConfig) return enveloppeConfig;
    // override enveloppe config with produit config
    const mergedConfig = enveloppeConfig.fieldConfigs.map((item: FieldConfig) => {
      const produitOverride = produitConfig.fieldConfigs.find(override => override.fieldKey === item.fieldKey);
      return produitOverride ?? item;
    });
    // then add the rest of selected product config of the fields
    produitConfig.fieldConfigs.forEach(item => {
      if (!mergedConfig.find(result => result.fieldKey === item.fieldKey)) {
        mergedConfig.push(item);
      }
    });

    return {
      fieldConfigs: mergedConfig,
      lastChangeDate: this.getMostRecentDate(
        new Date(enveloppeConfig.lastChangeDate),
        new Date(produitConfig.lastChangeDate)
      ),
    } as AdequationConfig;
  }
  getMostRecentDate(date1: Date, date2: Date) {
    return date1 > date2 ? date1 : date2;
  }

  async deprecateStorageFile(storageFileId: string) {
    const result = await firstValueFrom(
      this.queryManager.mutate<{ deprecateStorageFile: StorageFile }>({
        mutation: DEPRECATE_STORAGEFILE,
        variables: { storageFileId },
      })
    );
    return result.data?.deprecateStorageFile;
  }
}
